import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useMemo, useState } from "react";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { useWidgetOrigin } from "../embed/useWidgetOrigin";
import {
  META_LANGS,
  META_PLATFORMS,
  buildMetaReserveUrl,
  type MetaLang,
  type MetaPlatform,
} from "./deepLink";

/**
 * `/v/:venueId/settings/meta-reserve` — the social "Réserver" entry point (tasks/meta-reserve.md).
 * A manager copies a META-tagged deep link and pastes it into their Instagram and/or Facebook profile;
 * a booking arriving through it is attributed to the canonical `META` `BookingChannel` (DOMAIN.md §B.6).
 * MANAGER/root-gated by the route (RequireVenueRole).
 *
 * Instagram and Facebook are surfaced INDEPENDENTLY (spec Open question 2): each is a local toggle, so a
 * venue present on only one platform copies just that link, and un-toggling one leaves the other — and
 * every already-`META`-tagged booking (channel frozen at creation, BK-3) — untouched. Everything is PURE
 * CLIENT-SIDE composition: the link encodes only slug + `channel=meta` (+ optional lang); there is no
 * meta_connection table and no backend call (spec Tech decisions §Persistence). The native in-profile
 * Reserve button is deferred behind `integrations/meta` `MetaListingPort` — recorded in the note below,
 * not shipped here.
 */
export function MetaReserveSettings() {
  const { venue } = useVenue();
  const { t } = useTranslation();
  const [lang, setLang] = useState<MetaLang>("auto");
  // Local-only presence toggles — there is no server state to persist (spec §Persistence).
  const [present, setPresent] = useState<Record<MetaPlatform, boolean>>({
    instagram: true,
    facebook: true,
  });
  const [copied, setCopied] = useState<MetaPlatform | null>(null);

  const origin = useWidgetOrigin();

  const reserveUrl = useMemo(
    () => buildMetaReserveUrl({ origin, slug: venue.slug, lang }),
    [origin, venue.slug, lang],
  );

  async function copy(value: string, which: MetaPlatform) {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(which);
      window.setTimeout(() => setCopied((c) => (c === which ? null : c)), 2000);
    } catch {
      // Clipboard denied/unavailable: the link stays selectable in the field — never a dead end.
    }
  }

  return (
    <PageScaffold title={`${t("metaReserve.title")} — ${venue.displayName}`}>
      <div className={css({ display: "flex", flexDirection: "column", gap: "8", maxW: "3xl" })}>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("metaReserve.intro")}</p>

        <label
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "1",
            fontSize: "sm",
            maxW: "xs",
          })}
        >
          <span className={css({ fontWeight: "semibold" })}>{t("metaReserve.lang.legend")}</span>
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value as MetaLang)}
            className={css({ borderWidth: "1px", rounded: "md", px: "2", py: "1.5" })}
          >
            {META_LANGS.map((l) => (
              <option key={l} value={l}>
                {t(`metaReserve.lang.${l}`)}
              </option>
            ))}
          </select>
        </label>

        {META_PLATFORMS.map((platform) => (
          <section
            key={platform}
            data-testid={`meta-platform-${platform}`}
            className={css({ display: "flex", flexDirection: "column", gap: "2" })}
          >
            <label
              className={css({
                display: "flex",
                gap: "2",
                alignItems: "center",
                fontSize: "lg",
                fontWeight: "semibold",
                cursor: "pointer",
              })}
            >
              <input
                type="checkbox"
                checked={present[platform]}
                onChange={(e) => setPresent((p) => ({ ...p, [platform]: e.target.checked }))}
              />
              <span>{t(`metaReserve.platform.${platform}`)}</span>
            </label>

            {present[platform] ? (
              <>
                <p className={css({ fontSize: "sm", color: "fg.muted" })}>
                  {t(`metaReserve.platform.${platform}Hint`)}
                </p>
                <a
                  href={reserveUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`meta-url-${platform}`}
                  className={css({ fontSize: "sm", fontFamily: "mono", wordBreak: "break-all" })}
                >
                  {reserveUrl}
                </a>
                <div className={css({ display: "flex", gap: "3", alignItems: "center" })}>
                  <button
                    type="button"
                    onClick={() => void copy(reserveUrl, platform)}
                    className={css({
                      px: "3",
                      py: "1.5",
                      borderWidth: "1px",
                      rounded: "md",
                      fontSize: "sm",
                      cursor: "pointer",
                      bg: "colorPalette.default",
                      color: "colorPalette.fg",
                    })}
                  >
                    {t("metaReserve.copy")}
                  </button>
                  <output
                    aria-live="polite"
                    className={css({ fontSize: "sm", color: "fg.success" })}
                  >
                    {copied === platform ? t("metaReserve.copied") : ""}
                  </output>
                </div>
              </>
            ) : (
              <p className={css({ fontSize: "sm", color: "fg.muted" })}>
                {t("metaReserve.platform.absentHint")}
              </p>
            )}
          </section>
        ))}

        <p role="note" className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("metaReserve.tag.note")}
        </p>
        <p role="note" className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("metaReserve.native.note")}
        </p>
      </div>
    </PageScaffold>
  );
}
